import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import {
  useGetBagDetailsQuery,
  useUpdateBagMutation,
  useUploadBagImagesMutation,
} from '../../slices/bagsApiSlice';

const BagStaffEditScreen = () => {
  const { id: bagId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    date: '',
    status: '',
    accountName: '',
    details: '',
    number: '',
  });

  const [images, setImages] = useState([]);
  const { data: bag, isLoading, error, refetch } = useGetBagDetailsQuery(bagId);
  const [updateBag] = useUpdateBagMutation();
  const [uploadBagImages] = useUploadBagImagesMutation();

  useEffect(() => {
    if (bag) {
      setFormData({
        date: bag.date ? new Date(bag.date).toISOString().split('T')[0] : '',
        status: bag.status || '',
        accountName: bag.accountName || '',
        details: bag.details || '',
        number: bag.number || '',
      });
      setImages(bag.images || []);
    }
  }, [bag]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File size exceeds 5MB: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const uploadImageHandler = async (files) => {
    if (!validateFiles(files)) return;

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('images', file));

    try {
      const res = await uploadBagImages(formData).unwrap();
      toast.success(res.message);
      setImages([...images, ...res.images]);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const removeImageHandler = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await updateBag({
        bagId,
        ...formData,
        images,
      }).unwrap();
      toast.success('Bag updated successfully');
      refetch();
      navigate('/staffnote');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  return (
    <div className="container mt-4">
      <h1>Edit Bag</h1>
      <Link to='/staffnote' className='btn btn-light my-3'>
        Go Back
      </Link>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'Error loading bag details'}</Message>
      ) : (
        <Form onSubmit={submitHandler}>
          <Form.Group controlId="date" className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group controlId="status" className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Control
              as="select"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="Completed">Completed</option>
              <option value="Pending">Pending</option>
            </Form.Control>
          </Form.Group>

          <Form.Group controlId="accountName" className="mb-3">
            <Form.Label>Account Name</Form.Label>
            <Form.Control
              type="text"
              name="accountName"
              placeholder="Enter account name"
              value={formData.accountName}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group controlId="details" className="mb-3">
            <Form.Label>Details</Form.Label>
            <Form.Control
              type="text"
              name="details"
              placeholder="Enter details"
              value={formData.details}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group controlId="number" className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              name="number"
              placeholder="Enter number"
              value={formData.number}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group controlId="images" className="mb-3">
            <Form.Label>Images</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={(e) => uploadImageHandler(e.target.files)}
            />
            <div className="mt-3">
              {images.map((img, idx) => (
                <div key={idx} style={{ display: 'inline-block', position: 'relative', marginRight: '10px' }}>
                  <img
                    src={img}
                    alt={`Bag ${idx}`}
                    className="img-fluid"
                    style={{ maxWidth: '100px' }}
                  />
                  <button
                    onClick={() => removeImageHandler(idx)}
                    style={{ position: 'absolute', top: '0', right: '0' }}
                  >
                    ✖
                  </button>
                </div>
              ))}
            </div>
          </Form.Group>

          <Button type="submit" variant="primary">
            Update
          </Button>
        </Form>
      )}
    </div>
  );
};

export default BagStaffEditScreen;